import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, Textarea } from "@libs/zapi-ui/shadcn";
import { AppButton, FigmaTypography } from "@libs/zapi-ui/custom";
import { FileDashedIcon, FileIcon } from "@phosphor-icons/react";
import { formatCharacterLength, formatCompressionRatio } from "@/zapi-frontend/shared/lib/formatCharacterLength";

type TDocumentContentModalProps = {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  fileName: string;
  fullContentDisplay: string;
  editedSummaryContent: string;
  onEditedSummaryChange: (value: string) => void;
  hasSummarizedContent: boolean;
  onSaveContent: () => void;
  onGenerateSummary: () => void;
  isSavingContent: boolean;
  isGeneratingSummary: boolean;
  fullContentDisplayLength: number;
  editedSummaryContentLength: number;
  readOnly?: boolean;
};

export function DocumentContentModal(props: TDocumentContentModalProps) {
  const {
    isOpen,
    onOpenChange,
    fileName,
    fullContentDisplay,
    editedSummaryContent,
    onEditedSummaryChange,
    hasSummarizedContent,
    onSaveContent,
    onGenerateSummary,
    isSavingContent,
    isGeneratingSummary,
    fullContentDisplayLength,
    editedSummaryContentLength,
    readOnly = false
  } = props;

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-7xl h-[95vh] max-h-[95vh] flex flex-col p-6">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>Document Content: {fileName}</span>
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-hidden flex flex-col gap-4 min-h-0">
          <div className="flex gap-4 h-full min-h-0">
            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                <FileIcon className="w-4 h-4 text-gray-600" />
                <FigmaTypography variant="label-2" className="font-medium">
                  Full Content (As Sent to AI)
                </FigmaTypography>
                <FigmaTypography variant="body-3" color="zinc500">
                  ({formatCharacterLength(fullContentDisplayLength)})
                </FigmaTypography>
              </div>
              <Textarea
                value={fullContentDisplay}
                readOnly
                className="flex-1 font-mono text-[12px] leading-[18px] resize-none bg-gray-50 cursor-default"
                placeholder="Full document content..."
                style={{ outline: "none", boxShadow: "none" }}
              />
            </div>

            <div className="flex-1 flex flex-col min-h-0">
              <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                <FileDashedIcon className="w-4 h-4 text-blue-600" />
                <FigmaTypography variant="label-2" className="font-medium">
                  Summary (As Sent to AI)
                </FigmaTypography>
                <FigmaTypography variant="body-3" color="zinc500">
                  ({formatCharacterLength(editedSummaryContentLength)})
                </FigmaTypography>

                <FigmaTypography variant="body-3" color="zinc400">
                  {formatCompressionRatio(fullContentDisplayLength, editedSummaryContentLength)}
                </FigmaTypography>
              </div>
              {hasSummarizedContent ? (
                <Textarea
                  data-testid="document-content-summary-input"
                  value={editedSummaryContent}
                  onChange={readOnly ? undefined : (e) => onEditedSummaryChange(e.target.value)}
                  readOnly={readOnly}
                  className={`flex-1 font-mono text-[12px] leading-[18px] resize-none ${readOnly ? "bg-gray-50 cursor-default" : "bg-white border-blue-200"}`}
                  placeholder="Summarized document content..."
                  style={{ outline: "none", boxShadow: "none" }}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center bg-gray-50 rounded border-2 border-dashed border-gray-300">
                  <div className="text-center">
                    <FigmaTypography variant="body-2" color="zinc500">
                      No summary available
                    </FigmaTypography>
                    <FigmaTypography variant="body-3" color="zinc400" className="mt-1">
                      Use the "Summarize" action to generate one
                    </FigmaTypography>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        <DialogFooter className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <FigmaTypography variant="body-3" color="zinc500">
              Full: {formatCharacterLength(fullContentDisplayLength)}
            </FigmaTypography>
            {hasSummarizedContent && (
              <FigmaTypography variant="body-3" color="zinc500">
                Summary: {formatCharacterLength(editedSummaryContentLength)}
              </FigmaTypography>
            )}
          </div>
          {!readOnly && (
            <div className="flex gap-2">
              {hasSummarizedContent ? (
                <AppButton
                  data-testid="document-content-save-summary-button"
                  appearance="contained"
                  onClick={onSaveContent}
                  disabled={isSavingContent}
                  isLoading={isSavingContent}
                  loadingText="Saving..."
                >
                  Save Summary
                </AppButton>
              ) : (
                <AppButton
                  data-testid="document-content-generate-summary-button"
                  appearance="contained"
                  onClick={onGenerateSummary}
                  disabled={isGeneratingSummary}
                  isLoading={isGeneratingSummary}
                  loadingText="Generating Summary..."
                >
                  Generate Summary
                </AppButton>
              )}
            </div>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default DocumentContentModal;
